import { z } from 'zod';

export type LogisticOperatorJsonConfig = Record<string, unknown>;

export const DEFAULT_GPS_TRACKING_VALUES = {
  enabled: false,
  from: '',
  to: '',
};

const TIME_FORMAT = /^([01]\d|2[0-3]):[0-5]\d$/;

export const gpsTrackingSchema = z
  .object({
    enabled: z.boolean(),
    from: z.string(),
    to: z.string(),
  })
  .superRefine((values, ctx) => {
    if (!values.enabled) return;

    const validFrom = TIME_FORMAT.test(values.from);
    const validTo = TIME_FORMAT.test(values.to);

    if (!validFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['from'],
        message: 'Indica una hora de inicio válida (HH:mm).',
      });
    }

    if (!validTo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'Indica una hora de fin válida (HH:mm).',
      });
    }

    if (validFrom && validTo && values.from === values.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'La hora de fin debe ser distinta de la hora de inicio.',
      });
    }
  });

export type GpsTrackingFormValues = z.infer<typeof gpsTrackingSchema>;

export type LogisticOperatorGpsTrackingConfig = Partial<GpsTrackingFormValues> &
  Record<string, unknown>;

const isConfigObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const getGpsTrackingFormValues = (
  jsonConfig?: LogisticOperatorJsonConfig | null
): GpsTrackingFormValues => {
  const gpsTracking = jsonConfig?.gpsTracking;
  if (!isConfigObject(gpsTracking)) return { ...DEFAULT_GPS_TRACKING_VALUES };

  return {
    enabled: gpsTracking.enabled === true,
    from: typeof gpsTracking.from === 'string' ? gpsTracking.from : '',
    to: typeof gpsTracking.to === 'string' ? gpsTracking.to : '',
  };
};

export const mergeGpsTrackingConfig = (
  values: GpsTrackingFormValues,
  originalJsonConfig?: LogisticOperatorJsonConfig | null
): LogisticOperatorJsonConfig | null => {
  const originalValues = getGpsTrackingFormValues(originalJsonConfig);
  if (
    values.enabled === originalValues.enabled &&
    values.from === originalValues.from &&
    values.to === originalValues.to
  ) {
    return originalJsonConfig ?? null;
  }

  const originalGpsTracking = originalJsonConfig?.gpsTracking;
  const gpsTracking: LogisticOperatorGpsTrackingConfig = {
    ...(isConfigObject(originalGpsTracking) ? originalGpsTracking : {}),
    enabled: values.enabled,
    from: values.from,
    to: values.to,
  };

  return {
    ...originalJsonConfig,
    gpsTracking,
  };
};
